/**
 * @copyright
 * (c) 2026 netTrek GmbH & Co. KG
 *
 * Normalizes a decoded C2PA claim (`c2pa.claim` v1 or `c2pa.claim.v2`) into a
 * version-agnostic shape. v1 lists every assertion in `assertions`; v2 splits
 * them into `created_assertions` / `gathered_assertions`.
 */

/** A hashed-URI reference to an assertion (or manifest part). */
export interface HashedUriRef {
    url: string
    /** Last path segment of the URI — the assertion label for assertion refs. */
    label: string
    alg?: string
    hash?: Uint8Array
}

export interface ParsedClaim {
    version: 1 | 2
    instanceId?: string
    title?: string
    format?: string
    /** Assertion-hashing algorithm (e.g. `sha256`), NOT the signature algorithm. */
    alg?: string
    /** v1 only: free-form claim generator string. */
    claimGenerator?: string
    /** Raw `claim_generator_info` (a single object or an array in CBOR). */
    claimGeneratorInfo?: unknown
    /** JUMBF URI of the signature box. */
    signatureRef?: string
    /** v2 `created_assertions`, or v1 `assertions`. */
    createdAssertions: HashedUriRef[]
    /** v2 `gathered_assertions` (empty for v1). */
    gatheredAssertions: HashedUriRef[]
    /** v2 `redacted_assertions` URIs. */
    redactedAssertions: string[]
    raw: Record<string, unknown>
}

function lastSegment (url: string): string {
    // e.g. "self#jumbf=c2pa.assertions/c2pa.actions.v2" → "c2pa.actions.v2"
    const i = url.lastIndexOf('/')
    return i >= 0 ? url.slice(i + 1) : url
}

function toRef (v: unknown): HashedUriRef | null {
    if (!v || typeof v !== 'object') return null
    const o = v as Record<string, unknown>
    const url = typeof o.url === 'string' ? o.url : null
    if (url === null) return null
    return {
        url,
        label: lastSegment(url),
        alg: typeof o.alg === 'string' ? o.alg : undefined,
        hash: o.hash instanceof Uint8Array ? o.hash : undefined
    }
}

function toRefs (v: unknown): HashedUriRef[] {
    if (!Array.isArray(v)) return []
    return v.map(toRef).filter((r): r is HashedUriRef => r !== null)
}

/** Parses a decoded claim object. `boxLabel` is the claim box label (`c2pa.claim` | `c2pa.claim.v2`). */
export function parseClaim (decoded: unknown, boxLabel: string | undefined): ParsedClaim {
    const raw = (decoded && typeof decoded === 'object' ? decoded : {}) as Record<string, unknown>
    const isV2 = boxLabel === 'c2pa.claim.v2' || 'created_assertions' in raw

    const str = (k: string): string | undefined => (typeof raw[k] === 'string' ? raw[k] as string : undefined)

    return {
        version: isV2 ? 2 : 1,
        instanceId: str('instanceID'),
        title: str('dc:title'),
        format: str('dc:format'),
        alg: str('alg'),
        claimGenerator: str('claim_generator'),
        claimGeneratorInfo: raw.claim_generator_info,
        signatureRef: str('signature'),
        createdAssertions: toRefs(isV2 ? raw.created_assertions : raw.assertions),
        gatheredAssertions: toRefs(isV2 ? raw.gathered_assertions : undefined),
        redactedAssertions: Array.isArray(raw.redacted_assertions)
            ? (raw.redacted_assertions as unknown[]).filter((x): x is string => typeof x === 'string')
            : [],
        raw
    }
}
